import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { ApiError } from './lib/api';
import { useMeta } from './lib/hooks';
import { ToastProvider } from './components/Toast';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { Login } from './pages/Login';
import { ErrorBoundary } from './components/ErrorBoundary';

const Home = lazy(() => import('./pages/Home'));
const Jobs = lazy(() => import('./pages/Jobs'));
const NewJob = lazy(() => import('./pages/NewJob'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const PrintIntake = lazy(() => import('./pages/PrintIntake'));
const PrintCloseoutReport = lazy(() => import('./pages/PrintCloseoutReport'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Search = lazy(() => import('./pages/Search'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const Billing = lazy(() => import('./pages/Billing'));
const AdminQueue = lazy(() => import('./pages/AdminQueue'));
const TechGroupQueue = lazy(() => import('./pages/TechGroupQueue'));
const Assets = lazy(() => import('./pages/Assets'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Rma = lazy(() => import('./pages/Rma'));
const Reports = lazy(() => import('./pages/Reports'));
const Ledger = lazy(() => import('./pages/Ledger'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const EvReports = lazy(() => import('./pages/EvReports'));
const EvUpload = lazy(() => import('./pages/EvUpload'));
const Decisions = lazy(() => import('./pages/Decisions'));
const PublicQuote = lazy(() => import('./pages/public/PublicQuote'));
const PublicTrack = lazy(() => import('./pages/public/PublicTrack'));
const JobCardPage = lazy(() => import('./pages/public/JobCardPage'));

// โหลดโค้ดหน้าอื่นล่วงหน้าตอนเครื่องว่าง (ลดเวลารอ "กำลังโหลด…" ตอนกดเมนู)
const PAGES = [
  () => import('./pages/Home'), () => import('./pages/Jobs'), () => import('./pages/NewJob'), () => import('./pages/JobDetail'),
  () => import('./pages/Schedule'), () => import('./pages/Customers'), () => import('./pages/CustomerDetail'), () => import('./pages/Billing'),
  () => import('./pages/Rma'), () => import('./pages/Assets'), () => import('./pages/Contracts'), () => import('./pages/Reports'),
  () => import('./pages/Ledger'), () => import('./pages/EvReports'),
];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
  },
});

/** ต้องล็อกอินก่อน — ถ้า session หมดอายุ API จะส่ง 401 แล้วพากลับหน้า PIN */
function Protected({ bare = false }: { bare?: boolean }) {
  const navigate = useNavigate();
  const meta = useMeta();
  useEffect(() => {
    const go = () => navigate('/login', { replace: true });
    window.addEventListener('setcom:unauthorized', go);
    return () => window.removeEventListener('setcom:unauthorized', go);
  }, [navigate]);
  useEffect(() => {
    if (!meta.data) return;
    const preload = () => PAGES.forEach((load) => void load().catch(() => undefined));
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(preload);
    else setTimeout(preload, 1500);
  }, [meta.data]);
  if (meta.isPending) return <Spinner label="กำลังเชื่อมต่อ… (ครั้งแรกของวันอาจใช้เวลาสักครู่)" />;
  if (meta.error instanceof ApiError && meta.error.status === 401) return <Navigate to="/login" replace />;
  return bare ? <Outlet /> : <Layout />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/q/:token" element={<PublicQuote />} />
              <Route path="/t/:token" element={<PublicTrack />} />
              <Route path="/jc/:token" element={<JobCardPage />} />
              <Route path="/jobs/:id/print" element={<PrintIntake />} />
              <Route path="/jobs/:id/closeout-report" element={<PrintCloseoutReport />} />
              {/* หน้าอัปรูปงาน EV ของช่าง — เต็มจอ ไม่มีเมนู */}
              <Route element={<Protected bare />}>
                <Route path="/ev-upload/:jobId" element={<EvUpload />} />
              </Route>
              <Route element={<Protected />}>
                <Route index element={<Home />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/new" element={<NewJob />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/search" element={<Search />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:id" element={<CustomerDetail />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/admin-queue" element={<AdminQueue />} />
                <Route path="/tech-group" element={<TechGroupQueue />} />
                <Route path="/assets" element={<Assets />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/rma" element={<Rma />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/ledger" element={<Ledger />} />
                <Route path="/ev-reports" element={<EvReports />} />
                <Route path="/decisions" element={<Decisions />} />
                <Route path="/settings/*" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
